import { type Nullable, hasValue } from './values';

export type SingleOrArray<T> = T[] | T;

export function getDiff(arrA: string[], arrB: string[]) {
  const setA = new Set(arrA);

  return arrB.filter((item) => !setA.has(item));
}

export function isEqual<T>(
  arrA: Nullable<T[]>,
  arrB: Nullable<T[]>,
  predicate: (itemA: T, itemB: T) => boolean = (a, b) => a === b
) {
  if (!arrA || !arrB) {
    return false;
  }

  return (
    arrA.length === arrB.length &&
    arrA.every((item, index) => {
      return predicate(item, arrB[index]);
    })
  );
}

/**
 * Return the original value if already is an array;
 * return a new list with the given item as the only element otherwise.
 * */
export function toArray<T>(value: SingleOrArray<T>): T[] {
  return Array.isArray(value) ? value : [value];
}

export type FilterMapCallback<T, K> = (item: T, index: number) => Nullable<K>;

/** An optimized helper for Array.filter().map(). */
export function filterMap<T, K>(arr: T[], mapFunction: FilterMapCallback<T, K>) {
  return arr.reduce<K[]>((acc, item, index) => {
    const value = mapFunction(item, index);

    if (hasValue(value)) {
      acc.push(value);
    }

    return acc;
  }, []);
}

type WithChildren<T> = T & { children?: WithChildren<T>[] };

export function recursiveMap<T, K extends Partial<T>>(list: WithChildren<T>[], map: (item: T) => K): WithChildren<K>[] {
  return list.map((item) => {
    const children = item.children ? recursiveMap(item.children, map) : undefined;

    const newItem = map(item);

    return children ? { ...newItem, children } : newItem;
  });
}

export function mapOrPush<T>(list: T[], predicate: (currentItem: T, targetItem: T) => boolean, item: T): T[] {
  let isFound = false;

  const updatedList = list.map((listItem) => {
    if (predicate(listItem, item)) {
      isFound = true;
      return item;
    }
    return listItem;
  });

  if (!isFound) {
    updatedList.push(item);
  }

  return updatedList;
}
